import constants from '@Common/Core/Constant';
import enums, { ConstantContext, FilterTransitionBetweenComparison, OperatorType, TypeKind } from '@Common/Enums';
import documentUtils from '@Common/Helpers/DocumentUtils';
import {
    AnotherKPIOperand,
    BaseExpressionElement,
    CalendarObject,
    CalendarOperand,
    ConditionalOperand,
    Constant,
    ConstantDocument,
    DashboardFilterOperand,
    DateAddBusinessFunctionOperand,
    DateDiffBusinessFunctionOperand,
    DirectExpressionOperand,
    FieldOperand,
    InstanceFilterDefinitionOperand,
    InstanceFilterIsReopenedOperand,
    InstanceFilterMissingStepsOperand,
    InstanceFilterPatternOperand,
    InstanceFilterStepExistsOperand,
    InstanceFilterSwimlaneGroupsOperand,
    InstanceFilterTransitionBetweenStepsOperand,
    IsBusinessDateFunctionOperand,
    IsFilterEmptyOperand,
    KnownVariable,
    MathExpression,
    NewVariableOperand,
    NullOperand,
    Operator,
    Parameter,
    ParameterOperand,
    ProcessFilterDefinition,
    ProcessPropertyOperand,
    RFunction,
    RFunctionOperand,
    SpecialAggregationFunctionOperand,
    SQLFunctionOperand,
    StoredConstant,
    SwimlaneGroup,
    UserFilterOperand,
    WhereInOperand
} from '@InstanceFactory';
import { SwimlaneGroupOperand } from '@Studio/Editors/ExpressionEditor/SwimlaneGroupOperand';

import { getMetricOperandName } from '@Studio/StatementVisitor';
import formatHelper from '@Common/Helpers/FormatHelper';
import Guid from '@Common/Core/Guid';

namespace statementsFactory {
    export interface NamedMetricOperand extends AnotherKPIOperand {
        name?: string;
    }

    export function createMathExpression(type: TypeKind = TypeKind.bool, elements: BaseExpressionElement[] = []): MathExpression {
        return {
            $type: MathExpression.typeName,
            isBoolean: type === TypeKind.bool,
            expressionElements: elements,
            useCaseUI: false
        };
    }

    export function createOperand(type, obj, expression?): any {
        switch (type) {
            case SQLFunctionOperand.typeName:
                return statementsFactory.createSqlFunction(obj);

            case DirectExpressionOperand.typeName:
                return statementsFactory.createDirectExpression(obj.value);

            case Constant.typeName:
                return obj.directly
                    ? statementsFactory.createDirectExpression(obj.value)
                    : statementsFactory.createConstant(obj.type, obj.value, obj.directly, obj.context);

            case Operator.typeName:
                return statementsFactory.createOperator(obj);

            case FieldOperand.typeName:
                return statementsFactory.createFieldOperand(obj.tableAlias, obj.fieldId, obj.type);

            case StoredConstant.typeName:
                return statementsFactory.createStoredConstant(obj.type, obj.id, obj.name);

            case ParameterOperand.typeName:
                return statementsFactory.createCustomQueryParameter(obj.parameterType, obj.parameter ?? obj.withField);

            case NewVariableOperand.typeName:
                return statementsFactory.createVariable(obj.value);

            case KnownVariable.typeName:
                return statementsFactory.createKnownVariable(obj.value);

            case AnotherKPIOperand.typeName:
                return createMetricOperand(obj, expression);

            case SpecialAggregationFunctionOperand.typeName:
                return createSpecialAggregationFunction(obj);

            case DateDiffBusinessFunctionOperand.typeName:
                return createDateDiffBusinessFunction(obj);

            case DateAddBusinessFunctionOperand.typeName:
                return createDateAddBusinessFunction(obj);

            case IsBusinessDateFunctionOperand.typeName:
                return createIsBusinessDateFunction(obj);

            case CalendarOperand.typeName:
                return createCalendar(obj);

            case ConditionalOperand.typeName:
                return createConditionalOperand();

            case WhereInOperand.typeName:
                const whereInOperand = WhereInOperand.create();
                whereInOperand.isNegative = !!obj.isNegative;
                return whereInOperand;

            case NullOperand.typeName:
                return createNullOperand();

            case ProcessPropertyOperand.typeName:
                return createProcessProperty(obj);

            case InstanceFilterIsReopenedOperand.typeName:
                return InstanceFilterIsReopenedOperand.create();

            case InstanceFilterMissingStepsOperand.typeName:
                return InstanceFilterMissingStepsOperand.create();

            case InstanceFilterPatternOperand.typeName:
                return InstanceFilterPatternOperand.create();

            case InstanceFilterStepExistsOperand.typeName:
                return InstanceFilterStepExistsOperand.create();

            case InstanceFilterTransitionBetweenStepsOperand.typeName:
                const conf = _.defaults(obj, { comparison: FilterTransitionBetweenComparison.atLeast });
                const filterBetweenOperand = InstanceFilterTransitionBetweenStepsOperand.create();
                filterBetweenOperand.comparison = conf.comparison;
                return filterBetweenOperand;

            case InstanceFilterDefinitionOperand.typeName:
                const pfdOperand = InstanceFilterDefinitionOperand.create();
                pfdOperand.processFilterDefinition = documentUtils.createDocumentReference({
                    $type: ProcessFilterDefinition.typeName,
                    id: obj.id,
                    name: obj.name
                });
                return pfdOperand;

            case InstanceFilterSwimlaneGroupsOperand.typeName:
                return InstanceFilterSwimlaneGroupsOperand.create();

            case constants.mathExpressionTypes.swimlaneGroup:
                const sgRef = documentUtils.createDocumentReference({
                    $type: SwimlaneGroup.typeName,
                    id: obj.id,
                    name: obj.name
                });
                return new SwimlaneGroupOperand(sgRef);
            case RFunctionOperand.typeName:
                return createRFunction(obj);
            case UserFilterOperand.typeName:
                return UserFilterOperand.create({ filter: documentUtils.createInsightReference(obj) });
            case DashboardFilterOperand.typeName:
                return DashboardFilterOperand.create();
            case IsFilterEmptyOperand.typeName:
                return IsFilterEmptyOperand.create({
                    argumentsStatements: [MathExpression.create()]
                });
            default:
                throw new Error('Unknown operand type');
        }
    }

    export function createConditionalOperand(): ConditionalOperand {
        const result = ConditionalOperand.create();
        result.cases = [
            {
                whenStatement: createMathExpression(),
                thenStatement: createMathExpression()
            }
        ];
        result.defaultStatement = createMathExpression();
        return result;
    }

    export function createSpecialAggregationFunction(func): SpecialAggregationFunctionOperand {
        return SpecialAggregationFunctionOperand.create({
            arguments: func.arguments,
            argumentsStatements: _.map(func.arguments, _ => createMathExpression()),
            name: func.name
        });
    }

    export function createCalendar(obj): CalendarOperand {
        return CalendarOperand.create({
            calendar: documentUtils.createInsightReference({
                $type: CalendarObject.typeName,
                id: obj.id,
                name: obj.name
            } as CalendarObject)
        });
    }

    export function createDateDiffBusinessFunction(func): DateDiffBusinessFunctionOperand {
        return DateDiffBusinessFunctionOperand.create({
            argumentsStatements: _.map(func.arguments, _ => createMathExpression()),
            arguments: func.arguments,
            name: func.name,
            type: func.type
        });
    }

    export function createDateAddBusinessFunction(func): DateAddBusinessFunctionOperand {
        return DateAddBusinessFunctionOperand.create({
            argumentsStatements: _.map(func.arguments, _ => createMathExpression()),
            arguments: func.arguments,
            name: func.name,
            type: func.type
        });
    }

    export function createIsBusinessDateFunction(func): IsBusinessDateFunctionOperand {
        return IsBusinessDateFunctionOperand.create({
            argumentsStatements: _.map(func.arguments, _ => createMathExpression()),
            arguments: func.arguments,
            name: func.name,
            type: func.type
        });
    }

    export function createProcessProperty(processProperty: { id: string }): ProcessPropertyOperand {
        const doNotApplyTT = [constants.process.isFinished, constants.process.violator];
        return ProcessPropertyOperand.create({
            propertyId: processProperty.id,
            applyTT: !_.includes(doNotApplyTT, processProperty.id)
        });
    }

    export function createMetricOperand(obj, expression): NamedMetricOperand {
        const metricId = obj.metric.id;
        const metricName = obj.metric.name;

        const metric = {
            $type: obj.parentMetric ? constants.documentReferenceTypes.slaveDocument : constants.documentReferenceTypes.metric,
            id: metricId,
            name: metricName,
            masterDocument: obj.parentMetric
                ? {
                      id: obj.parentMetric.id,
                      name: obj.parentMetric.name
                  }
                : null,
            isChanged: false
        };
        return _.merge(
            AnotherKPIOperand.create({
                id: Guid.newGuid(),
                metric: metric as any
            }),
            {
                name: getMetricOperandName(expression, metricName)
            }
        );
    }

    export function createKnownVariable(name): KnownVariable {
        return KnownVariable.create({
            name: name
        });
    }

    export function createVariable(name): NewVariableOperand {
        return NewVariableOperand.create({
            name: name,
            determinantStatement: createMathExpression()
        });
    }

    export function createFieldOperand(tableAlias, fieldId, valueType = TypeKind.string): FieldOperand {
        return FieldOperand.create({
            fieldId: fieldId,
            tableAlias: tableAlias
        });
    }

    export const emptyFieldOperand = FieldOperand.create({
        tableAlias: 'c02e6ddd-79cc-4726-bda1-9adad4741667'
    });

    export function createSqlFunctionByName(functionName: string): SQLFunctionOperand {
        const func = _.find(altosoft.model.sqlFunctions, (f: any) => f.name === functionName);
        return createSqlFunction(func);
    }

    export function createSqlFunction(func: { name: string; arguments: any[] }): SQLFunctionOperand {
        return SQLFunctionOperand.create({
            argumentsStatements: _.map(func.arguments, _ => createMathExpression()),
            arguments: func.arguments,
            name: func.name
        });
    }

    export function createCustomQueryParameter(queryParameterType: enums.CustomQueryParameter, withField: boolean): ParameterOperand;
    export function createCustomQueryParameter(queryParameterType: enums.CustomQueryParameter, parameter: { id; name }): ParameterOperand;
    export function createCustomQueryParameter(
        queryParameterType: enums.CustomQueryParameter,
        parameterOrWithField: { id; name } | boolean
    ): ParameterOperand {
        let parameter, field: FieldOperand;
        parameterOrWithField = parameterOrWithField ?? false;
        if (_.isBoolean(parameterOrWithField)) {
            if (parameterOrWithField === true) {
                field = emptyFieldOperand;
            }
        } else {
            parameter = documentUtils.createDocumentReference(parameterOrWithField, Parameter.typeName);
        }
        return ParameterOperand.create({
            queryParameterType,
            parameter: parameter,
            field
        });
    }

    export function createOperator(operatorType: OperatorType): Operator {
        return Operator.create({ operatorType });
    }

    export function createDirectExpression(values): DirectExpressionOperand {
        return DirectExpressionOperand.create({
            values: values
        });
    }

    export function createConstant(
        type: TypeKind,
        value: string | number | Date | boolean,
        directly: boolean = false,
        context: ConstantContext = ConstantContext.default
    ): Constant {
        let formattedValue: string = null;
        if (value != null) {
            if (type === TypeKind.dateTime && value instanceof Date) {
                formattedValue = formatHelper.formatDate(value, constants.invariantCultureDateTimeFormat);
            } else {
                formattedValue = value.toString();
            }
        }

        const constant = Constant.create();
        constant.valueType = type;
        constant.directValue = formattedValue;
        constant.directly = directly;
        constant.context = context;
        return constant;
    }

    export function createNullOperand(): NullOperand {
        return NullOperand.create();
    }

    // TODO: remove
    export function createCaseStatement(): MathExpression {
        const statement = createMathExpression();
        statement.useCaseUI = true;
        statement.expressionElements = [statementsFactory.createConditionalOperand()];

        return statement;
    }

    export function createStoredConstant(type: string, id: string, name: string): StoredConstant {
        return StoredConstant.create({
            constant: documentUtils.createDocumentReference({
                $type: ConstantDocument.typeName,
                id: id,
                name: name
            })
        });
    }

    export function createRFunction(rFunction: RFunction): RFunctionOperand {
        const operand = RFunctionOperand.create({
            function: documentUtils.createDocumentReference(rFunction)
        });
        operand.dataArguments = _.map(rFunction.dataArguments, arg => createMathExpression());
        operand.valueArguments = _.map(rFunction.valueArguments, arg => createMathExpression());
        return operand;
    }
}
export default statementsFactory;
